import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { ActivatedRoute } from "@angular/router";
import { Tools } from "@comunicare/comunicare-helper";
import {
  DataType,
  IFilter,
  IPreferenceUser,
  IPrescriptionInfosResponse,
  ITableParameter,
  PreferenceContext,
} from "@comunicare/comunicare-interfaces";
import moment from "moment";
import { Subject, Subscription } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import { Choice } from "@comunicare/comunicare-angular-filters-display";
import { Item } from "src/app/components/item-selector/item-selector.component";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { PrescriptionService } from "src/app/providers/prescription.service";
import { ResponsiveService } from "src/app/providers/responsive.service";
import { SessionService } from "src/app/providers/session.service";
import { PrescriptionsDataSource } from "./prescription-datasource";

@Component({
  selector: "app-prescription-list-page",
  templateUrl: "./prescription-list-page.component.html",
  styleUrls: ["./prescription-list-page.component.scss"],
})
export class PrescriptionListPageComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatTable) table: MatTable<IPrescriptionInfosResponse>;
  @ViewChild(MatSort) sort: MatSort;

  public isLoading = true;
  public dataSource: PrescriptionsDataSource;
  private preferences: ITableParameter;
  public prescriptions: IPrescriptionInfosResponse[] = [];
  private onDestroy$ = new Subject<void>();
  public prescriptionsCount: number;
  private healthcareServicesRefs: string[];
  private currentSubscription: Subscription;
  public currentServices: string[] = [];
  public currentPageSize: number;
  public availableColumnItems: Item[] = [
    { value: "lastname", display: "model.patient.name", checked: true },
    { value: "firstname", display: "model.patient.firstname", checked: true },
    { value: "status", display: "table.status", checked: true },
    { value: "category", display: "category", checked: true },
    { value: "dateRequest", display: "page.deleteRequestList.table.requestDate", checked: true },
  ];
  public displayedColumns = ["lastname", "firstname", "status", "category", "dateRequest"];
  public filters: IFilter[] = [];
  /** Raw active filters, kept in sync to feed `app-item-filter` via the `filterFor` pipe. */
  public activeFilters: IFilter[] = [];
  public DATA_TYPE = DataType;

  constructor(
    private prescriptionService: PrescriptionService,
    private route: ActivatedRoute,
    private sessionService: SessionService,
    private healthcareService: HealthcareserviceService,
    public responsiveService: ResponsiveService,
    private preferenceService: PreferenceService
  ) {
    this.preferences = this.route.snapshot.data.preferences;
  }
  ngAfterViewInit(): void {
    this.sort.sortChange.subscribe(() => {
      this.loadPrescriptions();
    });
  }

  ngOnInit(): void {
    this.dataSource = new PrescriptionsDataSource(this.prescriptionService);

    // Subscribe to services setup changes
    this.sessionService.servicesSetupWatch.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.updateHealthcareServicesRefsAndLoad();
    });

    // If services are already available at init time, load data immediately
    if (this.sessionService.currentService) {
      this.updateHealthcareServicesRefsAndLoad();
    }
  }

  private updateHealthcareServicesRefsAndLoad(): void {
    if (this.sessionService.currentService.reference === "all") {
      // Use all available service references
      this.healthcareServicesRefs = this.healthcareService.availableServices().map((s) => s.asReference.reference);
    } else {
      // Use only the current service reference
      this.healthcareServicesRefs = [this.sessionService.currentService?.reference];
    }

    // Load prescriptions
    this.loadPrescriptions();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private loadPrescriptions() {
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
    }

    this.currentSubscription = this.prescriptionService
      .getPendingPrescriptionCount({
        services: this.healthcareServicesRefs,
      })
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe((result) => {
        this.prescriptionsCount = result?.count;
        this.dataSource.loadData({
          pageNumber: 0,
          pageSize: this.currentPageSize,
          filters: this.getFiltersFormated(),
          services: this.healthcareServicesRefs,
          sortId: this.sort?.active,
          sortOrder: this.sort?.direction,
        });

        this.isLoading = false;
      });
  }

  public getFilter(propertyName: string): IFilter {
    return this.dataSource.getFilter(propertyName);
  }

  applyFilter(filter: IFilter): void {
    this.dataSource.setFilter(filter);
    this.filters = Tools.deepCopy(this.dataSource.getAllFilters());
    this.activeFilters = [...this.dataSource.getAllFilters()];
    this.loadPrescriptions();
    this.updatePreference();
  }

  clearFilter(): void {
    this.dataSource.clearFilter();
    this.filters = [];
    this.activeFilters = [];
    this.paginator.pageIndex = 0;
    this.loadPrescriptions();
    this.updatePreference();
  }
  /**
   * Preferences
   */
  public updatePreference(): Promise<void> {
    return this.preferenceService
      .update({
        context: PreferenceContext.PRESCRIPTIONS_LIST,
        parameters: {
          filters: this.dataSource.getAllFilters(),
          availableColumnItems: this.availableColumnItems,
          itemsPerPage: this.paginator.pageSize,
        } as ITableParameter,
      })
      .pipe(first())
      .toPromise()
      .then((parameters: IPreferenceUser) => {
        const param = parameters.preferences.find((p) => p.context === PreferenceContext.PRESCRIPTIONS_LIST);
        this.preferences = param.parameters;
      });
  }

  public async availableColumnChanged(_items: Item[]): Promise<void> {
    this.displayedColumns = this.availableColumnItems.filter((item) => item.checked).map((item) => item.value);
    await this.updatePreference();
  }

  private getFiltersFormated(): IFilter[] {
    const formatedFilters: IFilter[] = [];
    const filters = Tools.deepCopy(this.dataSource.getAllFilters());
    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        switch (filter.dataType) {
          case this.DATA_TYPE.DATE:
            // format date params to string
            filter.data.fromDate = moment(filter.data.fromDate).format("YYYY-MM-DD");
            filter.data.toDate = moment(filter.data.toDate).format("YYYY-MM-DD");
            break;
          case this.DATA_TYPE.CHOICE: {
            const values: Array<string> = [];
            const choices: Choice[] = filter.data ? filter.data : [];
            choices.forEach((choice) => {
              if (choice.checked) {
                values.push(choice.value);
              }
            });
            filter.data = {
              value: values,
            };
            break;
          }
        }
        formatedFilters.push(filter);
      });
    }
    this.filters = formatedFilters;
    return formatedFilters;
  }
}
